import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Sparkles, 
  Lock, 
  ArrowLeft, 
  CheckCircle2, 
  Workflow, 
  Code2, 
  Instagram, 
  Bot, 
  CalendarDays, 
  Users, 
  Webhook, 
  Building2,
  Layers,
  Megaphone,
  LayoutTemplate,
  Key,
  CalendarClock,
  LayoutGrid,
  Plug
} from 'lucide-react';
import { Button } from '@/components/Button';
import { Portal } from '@/components/Portal';

export interface PlanUpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  featureTitle: string;
  requiredPlan?: 'Growth' | 'Pro' | 'Elite' | 'WhatsApp API' | 'Instagram Automation' | string;
  description?: string;
  benefits?: Array<{ title: string; description?: string }>;
  icon?: React.ComponentType<{ className?: string }>;
}

const DEFAULT_FEATURE_CONFIGS: Record<string, {
  icon: React.ComponentType<{ className?: string }>;
  badge: string;
  defaultPlan: string;
  description: string;
  benefits: Array<{ title: string; description?: string }>;
}> = {
  'WhatsApp Campaigns': {
    icon: Megaphone,
    badge: 'GROWTH FEATURE',
    defaultPlan: 'Growth',
    description: 'Send bulk WhatsApp marketing, utility, and promotional broadcasts directly to thousands of customers.',
    benefits: [
      { title: 'Bulk Broadcast Messaging', description: 'Schedule and send campaigns to segmented contact lists.' },
      { title: 'Approved Meta Templates', description: 'Use interactive buttons, quick replies, and rich media.' },
      { title: 'Campaign Analytics', description: 'Track real-time sent, delivered, read, and CTR rates.' },
    ],
  },
  'WhatsApp Templates': {
    icon: LayoutTemplate,
    badge: 'GROWTH FEATURE',
    defaultPlan: 'Growth',
    description: 'Create and synchronize Meta WhatsApp Business message templates for notifications and campaigns.',
    benefits: [
      { title: 'Meta Template Sync', description: 'Draft, submit, and sync approved templates in seconds.' },
      { title: 'Header Media & Variables', description: 'Personalize templates with dynamic custom attributes.' },
      { title: 'Quick Reply Buttons', description: 'Add one-tap response buttons for high customer engagement.' },
    ],
  },
  'Custom Attributes': {
    icon: Key,
    badge: 'GROWTH FEATURE',
    defaultPlan: 'Growth',
    description: 'Store custom customer metadata and dynamically substitute personalized variables in templates and chats.',
    benefits: [
      { title: 'Custom Contact Fields', description: 'Track order IDs, balances, dates, and custom notes.' },
      { title: 'Dynamic Variable Injection', description: 'Inject attributes seamlessly into automated responses.' },
      { title: 'Audience Segmentation', description: 'Filter contacts by attribute values for targeted outreach.' },
    ],
  },
  'Reminders': {
    icon: CalendarClock,
    badge: 'PRO FEATURE',
    defaultPlan: 'Pro',
    description: 'Schedule automated date and time based reminders to follow up with clients on WhatsApp.',
    benefits: [
      { title: 'Date-Triggered Reminders', description: 'Send automated alerts before appointments or payment dues.' },
      { title: 'Recurring Follow-ups', description: 'Set daily, weekly, or monthly check-in messages.' },
      { title: 'Automated Status Updates', description: 'Update reminder states automatically upon reply.' },
    ],
  },
  'WhatsApp CRM & Contacts': {
    icon: Users,
    badge: 'GROWTH FEATURE',
    defaultPlan: 'Growth',
    description: 'Manage your WhatsApp contacts, track conversations, and build an organised customer database — not available on the Instagram-only plan.',
    benefits: [
      { title: 'Unlimited Contacts', description: 'Store and manage all your WhatsApp contacts in one place.' },
      { title: 'Contact Attributes & Tags', description: 'Segment customers with custom tags and dynamic data fields.' },
      { title: 'Bulk Import & Export', description: 'Upload CSV contact lists and export your entire database.' },
    ],
  },
  'Sales CRM': {
    icon: LayoutGrid,
    badge: 'GROWTH FEATURE',
    defaultPlan: 'Growth',
    description: 'Manage your leads and customer pipelines with a visual Kanban sales CRM directly inside WhatsApp.',
    benefits: [
      { title: 'Visual Kanban Deal Stages', description: 'Move deals across customizable sales pipelines.' },
      { title: 'Lead Qualification & Notes', description: 'Log call summaries, deals value, and team notes.' },
      { title: 'Direct WhatsApp Chat Access', description: 'Open live chats directly from any deal card.' },
    ],
  },
  'Workflows & Automation': {
    icon: Workflow,
    badge: 'PRO FEATURE',
    defaultPlan: 'Pro',
    description: 'Automate customer conversations with visual flow builders, multi-step triggers, and drip campaign sequences.',
    benefits: [
      { title: 'Visual Flow & Form Builder', description: 'Drag-and-drop conversational logic without code.' },
      { title: 'Scheduled Drip Sequences', description: 'Nurture leads over time with automated message flows.' },
      { title: 'Interactive WhatsApp Buttons & Lists', description: 'Boost engagement with native interactive messages.' },
    ],
  },
  'Drip Campaigns': {
    icon: Workflow,
    badge: 'PRO FEATURE',
    defaultPlan: 'Pro',
    description: 'Build automated multi-day messaging sequences to follow up with leads and increase conversion rates.',
    benefits: [
      { title: 'Automated Time Delays', description: 'Send timed follow-ups 1 hour, 1 day, or 1 week later.' },
      { title: 'Targeted Contact Segments', description: 'Filter and enroll specific tags and attributes.' },
      { title: 'Performance Analytics', description: 'Track delivery, open, and response rates per step.' },
    ],
  },
  'Developer API & Webhooks': {
    icon: Code2,
    badge: 'PRO & API PLAN',
    defaultPlan: 'WhatsApp API',
    description: 'Integrate WhatsApp Cloud API directly into your backend with secure API keys, webhooks, and analytics.',
    benefits: [
      { title: 'Direct WhatsApp REST API', description: 'Send template messages and media via HTTP endpoints.' },
      { title: 'Real-Time Delivery Webhooks', description: 'Receive sent, delivered, read, and inbound events.' },
      { title: 'API Key Management & Analytics', description: 'Create multiple keys with IP and rate limit monitoring.' },
    ],
  },
  'Instagram Automation': {
    icon: Instagram,
    badge: 'INSTAGRAM PLAN',
    defaultPlan: 'Instagram Automation',
    description: 'Automate Instagram DM responses, reply to post comments automatically, and verify followers instantly.',
    benefits: [
      { title: 'Post & Reel Comment Auto-Reply', description: 'Trigger automatic responses whenever someone comments.' },
      { title: 'Keyword Triggered DMs', description: 'Send instant DM download links on specific keyword triggers.' },
      { title: 'Follower Verification', description: 'Ensure users follow your account before receiving rewards.' },
    ],
  },
  'AI Agents & Knowledge Bases': {
    icon: Bot,
    badge: 'GROWTH FEATURE',
    defaultPlan: 'Growth',
    description: 'Train AI agents on your website pages, PDFs, and documents to answer customer questions 24/7.',
    benefits: [
      { title: 'Website & Document Crawling', description: 'Upload PDFs or sync URLs for accurate responses.' },
      { title: 'Human Hand-off & Live Chat', description: 'Seamless transfer to human agents when needed.' },
      { title: 'Autonomous Conversation History', description: 'Full lead capture and analytics on customer queries.' },
    ],
  },
  'Appointments & Scheduling': {
    icon: CalendarDays,
    badge: 'PRO FEATURE',
    defaultPlan: 'Pro',
    description: 'Enable customers to book time slots and meetings directly through automated WhatsApp flows.',
    benefits: [
      { title: 'Automated Calendar Sync', description: 'Eliminate double-booking with real-time slot management.' },
      { title: 'WhatsApp Booking Reminders', description: 'Reduce no-shows with automated WhatsApp notifications.' },
      { title: 'Multi-Staff Scheduling', description: 'Assign bookings to specific team members.' },
    ],
  },
  'Live Agents & Roles': {
    icon: Users,
    badge: 'PRO FEATURE',
    defaultPlan: 'Pro',
    description: 'Add multiple team members, configure custom roles & permissions, and enable Round Robin lead assignment.',
    benefits: [
      { title: 'Multi-Agent Team Inbox', description: 'Allow multiple agents to reply simultaneously from 1 number.' },
      { title: 'Role-Based Access Control', description: 'Fine-grained permissions per department and agent.' },
      { title: 'Round Robin Assignment', description: 'Distribute incoming chats automatically across active agents.' },
    ],
  },
  'Webhooks Integration': {
    icon: Webhook,
    badge: 'ELITE FEATURE',
    defaultPlan: 'Elite',
    description: 'Stream live WhatsApp message statuses and inbound payloads directly to your custom webhook endpoints.',
    benefits: [
      { title: 'Real-time Event Streams', description: 'Instant webhook payloads on Sent, Delivered, and Read.' },
      { title: 'HMAC-SHA256 Signatures', description: 'Cryptographic payload verification for maximum security.' },
      { title: 'Payload Logs & Retries', description: 'Inspect failed deliveries and retry webhooks.' },
    ],
  },
  'Real Estate Builder': {
    icon: Building2,
    badge: 'PRO FEATURE',
    defaultPlan: 'Pro',
    description: 'Create interactive property listings, floor plans, and real estate catalogs on WhatsApp.',
    benefits: [
      { title: 'Property Catalog Sync', description: 'Showcase units, pricing, and amenities directly in chat.' },
      { title: 'Automated Lead Qualification', description: 'Capture budget, location, and timeline preferences.' },
      { title: 'Site Visit Scheduling', description: 'Book property tours and site visits automatically.' },
    ],
  },
  'Third-Party Integrations': {
    icon: Plug,
    badge: 'API & PRO FEATURE',
    defaultPlan: 'WhatsApp API',
    description: 'Connect payment gateways (Razorpay, PayU, Zaakpay, UPI) and third-party tools to automate transactions over WhatsApp.',
    benefits: [
      { title: 'Payment Gateway Integrations', description: 'Accept Razorpay, PayU, Zaakpay, and UPI directly in chat.' },
      { title: 'Automated Order Receipts', description: 'Trigger real-time instant payment confirmation messages.' },
      { title: 'Multi-Gateway Switching', description: 'Seamlessly toggle and test sandbox & production gateways.' },
    ],
  },
};

export const PlanUpgradeModal: React.FC<PlanUpgradeModalProps> = ({
  isOpen,
  onClose,
  featureTitle,
  requiredPlan,
  description,
  benefits,
  icon: CustomIcon,
}) => {
  const navigate = useNavigate();

  const config = DEFAULT_FEATURE_CONFIGS[featureTitle] || {
    icon: Layers,
    badge: `${(requiredPlan || 'PRO').toUpperCase()} FEATURE`,
    defaultPlan: requiredPlan || 'Pro',
    description: description || 'Upgrade your subscription plan to unlock this advanced feature and elevate your workflows.',
    benefits: benefits || [
      { title: 'Higher Limits & Capabilities', description: 'Expand your account boundaries.' },
      { title: 'Advanced Automation Tools', description: 'Automate tasks and save hours of manual effort.' },
      { title: 'Priority Support', description: 'Dedicated assistance whenever you need help.' },
    ],
  };

  const IconComponent = CustomIcon || config.icon;
  const displayPlan = requiredPlan || config.defaultPlan;
  const displayDescription = description || config.description;
  const displayBenefits = benefits || config.benefits;

  if (!isOpen) return null;

  const handleUpgrade = () => {
    navigate('/plans');
  };

  return (
    <Portal>
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
        {/* Backdrop */}
        <div 
          className="absolute inset-0 bg-black/50 backdrop-blur-xs transition-opacity animate-in fade-in duration-200"
          onClick={onClose}
        />

        {/* Modal Dialog Card */}
        <div className="relative z-10 w-full max-w-lg overflow-hidden rounded-xl bg-white dark:bg-slate-900 shadow-2xl border border-slate-200 dark:border-slate-800 animate-in zoom-in-95 duration-200">
          {/* Top Gradient Banner */}
          <div className="relative bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 px-6 py-6 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-white/15 backdrop-blur-md shadow-inner text-white border border-white/20">
                  <IconComponent className="h-6 w-6" />
                </div>
                <div>
                  <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wider bg-white/20 text-white border border-white/30 mb-0.5">
                    <Lock className="w-3 h-3" />
                    <span>{config.badge}</span>
                  </div>
                  <h3 className="text-lg font-bold text-white leading-tight">
                    Unlock {featureTitle}
                  </h3>
                </div>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="px-6 py-5 space-y-4">
            <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
              {displayDescription}
            </p>

            {/* Benefits List */}
            <div className="space-y-2.5 rounded-lg bg-slate-50 dark:bg-slate-800/50 p-3.5 border border-slate-100 dark:border-slate-800">
              <p className="text-xs font-bold text-slate-700 dark:text-slate-200 uppercase tracking-wider">
                What's included in {displayPlan}:
              </p>
              {displayBenefits.map((item, idx) => (
                <div key={idx} className="flex items-start gap-2.5 text-left">
                  <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" />
                  <div>
                    <span className="text-xs font-semibold text-slate-900 dark:text-slate-100">
                      {item.title}
                    </span>
                    {item.description && (
                      <p className="text-[11px] text-slate-500 dark:text-slate-400">
                        {item.description}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Footer Actions */}
          <div className="flex items-center justify-between gap-3 px-6 py-4 bg-slate-50/80 dark:bg-slate-900/80 border-t border-slate-200 dark:border-slate-800">
            <Button
              variant="outline"
              size="sm"
              onClick={onClose}
              className="gap-1.5 text-slate-700 dark:text-slate-300 border-slate-300 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Go Back</span>
            </Button>

            <Button
              variant="default"
              size="sm"
              onClick={handleUpgrade}
              className="gap-2 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white shadow-md font-semibold px-5"
            >
              <Sparkles className="w-4 h-4" />
              <span>View Plans & Upgrade</span>
            </Button>
          </div>
        </div>
      </div>
    </Portal>
  );
};

export default PlanUpgradeModal;
